import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  Table,
  InputNumber,
  Button,
  Space,
  Typography,
  Card,
  Alert,
  Checkbox,
  message,
  Select,
} from 'antd';
import {
  RollbackOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import type { SalesOrder, SalesOrderItem } from '../../types';
import apiService from '../../../../services/apiService';

const { Title, Text } = Typography;
const { Option } = Select;

interface QuickReturnModalProps {
  visible: boolean;
  onCancel: () => void;
  order: SalesOrder | null;
  onReturnCreated: (returnData: any) => void;
}

interface QuickReturnItem extends SalesOrderItem {
  returnQuantity: number;
  selected: boolean;
  availableToReturn: number;
}

const QuickReturnModal: React.FC<QuickReturnModalProps> = ({
  visible,
  onCancel,
  order,
  onReturnCreated,
}) => {
  const [form] = Form.useForm();
  const [returnItems, setReturnItems] = useState<QuickReturnItem[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (order && visible) {
      const items: QuickReturnItem[] = order.items.map(item => ({
        ...item,
        returnQuantity: 1,
        selected: false,
        availableToReturn: (item.deliveredQuantity || item.quantity) - (item.returnedQuantity || 0),
      }));
      setReturnItems(items);
      
      form.resetFields();
      form.setFieldsValue({
        reason: 'damaged_in_shipping'
      });
    }
  }, [order, visible, form]);

  const handleItemChange = (index: number, field: keyof QuickReturnItem, value: any) => {
    const newItems = [...returnItems];
    newItems[index] = { ...newItems[index], [field]: value };
    
    if (field === 'selected' && !value) {
      newItems[index].returnQuantity = 1;
    }
    
    setReturnItems(newItems);
  };

  const getSelectedItems = () => {
    return returnItems.filter(item => item.selected && item.returnQuantity > 0);
  };

  const calculateTotalRefund = () => {
    return getSelectedItems().reduce((total, item) => {
      return total + (item.returnQuantity * item.unitPrice);
    }, 0);
  };

  const handleSubmit = async () => {
    const selectedItems = getSelectedItems();
    if (selectedItems.length === 0) {
      message.error('請至少選擇一個商品進行退貨');
      return;
    }

    const values = await form.validateFields();
    setLoading(true);
    try {
      const payload = {
        originalOrderId: order!.id,
        customerId: order!.customerId,
        items: selectedItems.map(item => ({ productId: item.productId, quantity: item.returnQuantity, unitPrice: item.unitPrice })),
        reason: values.reason
      };
      const resp: any = await apiService.createReturn(payload);
      onReturnCreated(resp || payload);
      message.success('退貨申請已創建');
      onCancel();
    } catch (e) {
      message.error('退貨申請失敗');
    } finally {
      setLoading(false);
    }
  };

  const generateReturnNumber = (): string => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const random = Math.floor(Math.random() * 10000).toString().padStart(4, '0');
    return `RET-${year}${month}${day}-${random}`;
  };

  const columns: ColumnsType<QuickReturnItem> = [
    {
      title: '選擇',
      width: 60,
      render: (_, record, index) => (
        <Checkbox
          checked={record.selected}
          onChange={(e) => handleItemChange(index, 'selected', e.target.checked)}
          disabled={(record.availableToReturn || 0) <= 0}
        />
      ),
    },
    {
      title: '商品',
      dataIndex: 'product',
      render: (product: any, record) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>{product.name}</div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            SKU: {product.sku} | 可退: {record.availableToReturn}
          </div>
        </div>
      ),
    },
    {
      title: '退貨數量',
      width: 120,
      render: (_, record, index) => (
        <InputNumber
          min={1}
          max={record.availableToReturn || 0}
          value={record.returnQuantity}
          onChange={(value) => handleItemChange(index, 'returnQuantity', value || 1)}
          disabled={!record.selected}
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: '退款金額',
      width: 100,
      render: (_, record) => (
        <Text strong style={{ color: record.selected ? '#f5222d' : '#666' }}>
          ${(record.returnQuantity * record.unitPrice).toFixed(2)}
        </Text>
      ),
    },
  ];

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <RollbackOutlined />
          <span>快速退貨申請 - {order?.orderNumber}</span>
        </div>
      }
      open={visible}
      onCancel={onCancel}
      width={800}
      footer={
        <div style={{ textAlign: 'right' }}>
          <Space>
            <Button onClick={onCancel}>取消</Button>
            <Button type="primary" onClick={handleSubmit} loading={loading}>
              創建退貨申請
            </Button>
          </Space>
        </div>
      }
      destroyOnClose
    >
      <Alert
        message="快速退貨申請"
        description="選擇要退貨的商品創建申請，詳細處理將在退貨管理頁面進行。"
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
      />

      <Card title="選擇退貨商品">
        <Table
          columns={columns}
          dataSource={returnItems}
          rowKey="id"
          pagination={false}
          size="small"
          summary={() => {
            const selectedItems = getSelectedItems();
            const totalRefund = calculateTotalRefund();
            
            return selectedItems.length > 0 ? (
              <Table.Summary.Row style={{ backgroundColor: '#fafafa' }}>
                <Table.Summary.Cell index={0} colSpan={3}>
                  <Text strong>
                    已選擇 {selectedItems.length} 個商品
                  </Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={1}>
                  <Text strong style={{ color: '#f5222d' }}>
                    總計: ${totalRefund.toFixed(2)}
                  </Text>
                </Table.Summary.Cell>
              </Table.Summary.Row>
            ) : null;
          }}
        />
      </Card>

      <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
        <Card title="退貨原因">
          <Form.Item
            name="reason"
            label="選擇退貨原因"
            rules={[{ required: true, message: '請選擇退貨原因' }]}
          >
            <Select placeholder="選擇退貨原因">
              <Option value="damaged_in_shipping">運送損壞</Option>
              <Option value="defective_product">產品缺陷</Option>
              <Option value="wrong_item_sent">發錯商品</Option>
              <Option value="not_as_described">與描述不符</Option>
              <Option value="other">其他原因</Option>
            </Select>
          </Form.Item>
        </Card>
      </Form>
    </Modal>
  );
};

export default QuickReturnModal;
